/**
 * CF-BONUS-V3-WITHDRAW-WARNING-V1 — what the withdraw modal is allowed to do about the bonus.
 *
 * WHY THIS IS ITS OWN MODULE (and not a helper inside WithdrawModal.tsx):
 * this is the decision that stands between a player and the irreversible destruction of their
 * sticky chips. `backend/src/custodial/withdrawals.ts` calls `voidActiveBonusOnWithdrawalInTx`
 * inside the withdrawal transaction, and `bonus-engine.ts` then sets `sticky_nano='0'`,
 * `status='voided'`. There is no undo. A decision with that blast radius has to be provable
 * without mounting React, a DOM, or a network — so it lives here, pure, beside its test.
 *
 * Same shape as `growth-state.ts`: a pure projection core with a hermetic sibling test.
 */

// Explicit .ts extensions (tsconfig has allowImportingTsExtensions) so this module resolves under
// BOTH vite/tsc and a bare `node --experimental-strip-types` run. That is what makes the sibling
// test hermetic — no bundler, no DOM, no network. Both deps are leaf modules with zero imports of
// their own, so the extension is needed here and nowhere else.
import { remainingTurnoverNano, type BonusView } from './bonus-state.ts';
import { formatTonExact, parseNanoString } from './ton-format.ts';

/**
 * `checking`  we have not yet heard back. No withdrawal CTA is rendered.
 * `blocked`   we asked and could not get a trustworthy answer. FAIL CLOSED — no withdrawal CTA.
 *             Silence is not permission: an unknown state may hide active sticky chips that a
 *             withdrawal would destroy.
 * `clear`     server-confirmed: nothing is at risk. The modal behaves exactly as it always has.
 * `at_risk`   server-confirmed active bonus with sticky > 0. Warn, and make preserving it the
 *             default action.
 */
export type BonusGuard =
    | { kind: 'checking' }
    | { kind: 'blocked' }
    | { kind: 'clear' }
    | { kind: 'at_risk'; stickyTon: string; remainingTon: string | null };

/**
 * Pure projection from a bonus read to what the withdraw modal may do.
 *
 * Deliberately conservative in BOTH directions, and the asymmetry is the point:
 *  - we warn ONLY on a server-confirmed active bonus with a positive, parseable sticky balance.
 *    A warning that fires when nothing is at risk teaches players to dismiss the one that matters.
 *  - anything we cannot parse, cannot reach, or cannot authenticate is `blocked` — never `clear`.
 *    "We don't know" must never render as "you have nothing to lose".
 */
export function deriveBonusGuard(view: BonusView): BonusGuard {
    // Unreachable / unparseable / auth failure. We do NOT know whether chips are at risk.
    if (view.kind === 'unavailable') return { kind: 'blocked' };
    // `none` and every terminal state carry nothing a withdrawal can destroy.
    if (view.kind !== 'active') return { kind: 'clear' };

    const sticky = parseNanoString(view.stickyNano);
    // An active bonus whose sticky balance will not parse is an UNKNOWN, not a zero.
    if (sticky === null) return { kind: 'blocked' };
    if (sticky <= 0n) return { kind: 'clear' };

    // Remaining turnover is informational only — its absence must never suppress the warning.
    //
    // ⛔ We validate the INPUTS before trusting the output. `remainingTurnoverNano` returns the
    // string '0' when it cannot parse its arguments — indistinguishable from a genuine "wagering
    // complete". Rendering that would tell the player "0.00 TON more of play unlocks them", i.e.
    // you are already done, on the strength of data we could not read. A silent-zero helper must
    // never be allowed to speak for us. Caught by the sibling test before this shipped.
    const turnoverOk = parseNanoString(view.turnoverNano) !== null;
    const targetOk = parseNanoString(view.wrTargetNano) !== null;
    // Arithmetic still goes through the shared helper so this can never drift from BonusPanel's
    // figure; we only decide whether we are entitled to quote it at all.
    const remaining = (turnoverOk && targetOk)
        ? parseNanoString(remainingTurnoverNano(view.turnoverNano, view.wrTargetNano))
        : null;
    return {
        kind: 'at_risk',
        stickyTon: formatTonExact(sticky),
        remainingTon: remaining === null ? null : formatTonExact(remaining),
    };
}

/**
 * The single predicate the modal uses to decide whether a withdrawal POST may be attempted.
 * Exported so the render path and the `handleWithdraw` re-check cannot drift apart — both ask
 * this one function rather than each re-deriving the rule.
 */
export function mayAttemptWithdrawal(guard: BonusGuard): boolean {
    return guard.kind === 'clear' || guard.kind === 'at_risk';
}
